export enum SpreadsheetCellType {
  Empty = 'empty',
  StaticText = 'staticText',
  Metric = 'metric',
  Table = 'table'
}

export interface SpreadsheetCellPosition {
  row: number;
  column: number;
}

export interface SpreadsheetCellRange {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
}

export interface SpreadsheetStaticTextContent {
  text: string;
  style?: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
  };
}

export interface SpreadsheetMetricContent {
  datasetId: string;
  metricId: string;
  metricName: string;
  unit?: string;
  colorSettings?: {
    useConditionalColor: boolean;
    defaultColor: string;
  };
  calculatedValue?: number; // Stores the calculated value from calc-engine
}

export interface SpreadsheetTableDimension {
  datasetId: string;
  fieldId: string;
  fieldName: string;
  displayName?: string;
}

export interface SpreadsheetTableHeader {
  index: number;
  type: 'dimension' | 'metric';
  datasetId?: string;
  fieldId?: string;
  fieldName?: string;
  metricId?: string;
  metricName?: string;
  displayName: string;
  customName?: string; // User-defined custom name for the header
}

export interface SpreadsheetTableColorConfig {
  headerBackgroundColor: string;
  headerTextColor: string;
  groupingBackgroundColor: string;
  groupingTextColor: string;
  dataBackgroundColor1: string; // Primary data cell color
  dataBackgroundColor2: string; // Alternating data cell color
  dataTextColor: string;
}

export interface SpreadsheetTableContent {
  headerOrientation: 'horizontal' | 'vertical';
  groupByDataset: string;
  groupByField: string;
  groupByFieldName: string;
  groupByFieldCustomName?: string; // Custom name for the dimension column/row
  headers: SpreadsheetTableHeader[];
  colorConfig?: SpreadsheetTableColorConfig;
  calculatedData?: any[]; // Stores the calculated table data from calc-engine
  selectedRows?: number; // Number of rows selected for this table
  selectedColumns?: number; // Number of columns selected for this table
}

export interface SpreadsheetCellContent {
  type: SpreadsheetCellType;
  staticText?: SpreadsheetStaticTextContent;
  metric?: SpreadsheetMetricContent;
  table?: SpreadsheetTableContent;
}

export interface SpreadsheetCell {
  position: SpreadsheetCellPosition;
  content: SpreadsheetCellContent;
  span?: {
    rowSpan: number;
    colSpan: number;
  };
  style?: {
    border?: string;
    backgroundColor?: string;
    textAlign?: 'left' | 'center' | 'right';
  };
}

export interface SpreadsheetSelection {
  type: 'single' | 'range';
  startCell: SpreadsheetCellPosition;
  endCell?: SpreadsheetCellPosition;
}

export interface SpreadsheetBoardData {
  cells: { [key: string]: SpreadsheetCell }; // key format: "row-column"
  mergedCells?: SpreadsheetMergedCell[];
  rowCount: number;
  columnCount: number;
  defaultRowHeight: number;
  defaultColumnWidth: number;
  customRowHeights?: { [rowIndex: number]: number };
  customColumnWidths?: { [columnIndex: number]: number };
}

export interface SpreadsheetMergedCell {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
}

// Helper functions
export function getCellKey(row: number, column: number): string {
  return `${row}-${column}`;
}

export function getColumnLabel(columnIndex: number): string {
  let result = '';
  while (columnIndex >= 0) {
    result = String.fromCharCode(65 + (columnIndex % 26)) + result;
    columnIndex = Math.floor(columnIndex / 26) - 1;
  }
  return result;
}

export function getRowLabel(rowIndex: number): string {
  return (rowIndex + 1).toString();
}

export function parseCellKey(key: string): SpreadsheetCellPosition {
  const [row, column] = key.split('-').map(Number);
  return { row, column };
}

export function isPositionInRange(position: SpreadsheetCellPosition, range: SpreadsheetCellRange): boolean {
  return position.row >= range.startRow &&
         position.row <= range.endRow &&
         position.column >= range.startColumn &&
         position.column <= range.endColumn;
}
